import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../securityTab.module.scss';
import { Alert } from 'components/alert';
import { TwoFASetupData } from 'types/auth';
import { authService } from 'api/auth';
import { Skeleton } from '@mui/material';
import { ActionButton } from 'components/actionButton';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';

const TwoFactor: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [apiErrorTwoFA, setApiErrorTwoFA] = useState('');
  const [successMessageTwoFA, setSuccessMessageTwoFA] = useState('');
  const [twoFASetupData, setTwoFASetupData] = useState<TwoFASetupData | null>(null);
  const [isTwoFALoading, setIsTwoFALoading] = useState(false);
  const [isSetupLoading, setIsSetupLoading] = useState(false);
  const [isDisable2FA, setIsDisable2FA] = useState(false);
  const [code, setCode] = useState('');

  const fetch2FASetup = async () => {
    try {
      setIsSetupLoading(true);
      const response = await authService.get2FASetup();
      if (response.data) {
        setTwoFASetupData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch 2FA setup:', error);
    } finally {
      setIsSetupLoading(false);
    }
  };

  useEffect(() => {
    fetch2FASetup();
  }, []);

  const handleActivate2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length < 6) {
      setApiErrorTwoFA(t('settings.security.twoFa.enterValidCode'));
      return;
    }

    setIsTwoFALoading(true);
    setApiErrorTwoFA('');
    setSuccessMessageTwoFA('');

    try {
      const response = await authService.confirm2FA(twoFactorCode);
      if (response.status === 200) {
        setTwoFactorCode('');
        // Refresh setup data to show disable UI
        fetch2FASetup();
      } else {
        setApiErrorTwoFA(response.message || t('settings.security.twoFa.failedActivate'));
      }
    } catch (error: any) {
      setApiErrorTwoFA(error.response?.data?.message || t('settings.security.twoFa.failedActivate'));
    } finally {
      setIsTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!code) {
      setApiErrorTwoFA(t('settings.security.twoFa.enterCodeToDisable'));
      return;
    }

    setIsTwoFALoading(true);
    setApiErrorTwoFA('');
    setSuccessMessageTwoFA('');

    try {
      const response = await authService.disable2FA(code);
      if (response.status === 200) {
        setSuccessMessageTwoFA(t('settings.security.twoFa.disabledSuccess'));
        setCode('');
        setIsDisable2FA(false);
        fetch2FASetup();
      } else {
        setApiErrorTwoFA(response.message || t('settings.security.twoFa.failedDisable'));
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || t('settings.security.twoFa.failedDisable');
      setApiErrorTwoFA(errorMessage);
    } finally {
      setIsTwoFALoading(false);
    }
  };

  const handleDisableTwoFactor = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    setApiErrorTwoFA('');
  };

  return (
    <div className={styles.card}>
      <Alert
        message={apiErrorTwoFA || successMessageTwoFA}
        type={apiErrorTwoFA ? 'error' : 'success'}
        onClose={() => {
          setApiErrorTwoFA('');
          setSuccessMessageTwoFA('');
        }}
      />
      <h3 className={styles.cardTitle}>{t('settings.security.twoFa.title')}</h3>
      {twoFASetupData?.otpauthUrl === undefined &&
      twoFASetupData?.secret === undefined &&
      twoFASetupData?.message ? (
        <div className={styles.disableContainer}>
          <Alert message={twoFASetupData.message} type="success" variant="local" />
          <div className={styles.checkboxGroup}>
            <input
              type="checkbox"
              id="disable2fa"
              checked={isDisable2FA}
              onChange={(e) => setIsDisable2FA(e.target.checked)}
            />
            <label htmlFor="disable2fa"> {t('settings.security.twoFa.disableCheckbox')}</label>
          </div>

          {isDisable2FA && (
            <div className={styles.inputGroup} style={{ marginTop: '16px' }}>
              <label>{t('settings.security.twoFa.twoFactorCode')}</label>
              <div className={styles.inputWrapper}>
                <input
                  type={'text'}
                  className={styles.inputField}
                  placeholder="XXXXXX"
                  value={code}
                  onChange={handleDisableTwoFactor}
                />
              </div>
            </div>
          )}

          <ActionButton
            className={styles.activateBtn}
            style={{ marginTop: '16px' }}
            onClick={handleDisable2FA}
            disabled={!isDisable2FA || !code}
            isLoading={isTwoFALoading}
          >
            {t('settings.security.twoFa.disableBtn')}
          </ActionButton>
        </div>
      ) : (
        <>
          <div className={styles.twoFactorGrid}>
            <div className={styles.qrCodeWrapper}>
              {isSetupLoading || !twoFASetupData?.otpauthUrl ? (
                <Skeleton
                  animation="wave"
                  variant="rectangular"
                  width={120}
                  height={120}
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                />
              ) : (
                <div className={styles.qrPlaceholder}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(twoFASetupData.otpauthUrl)}`}
                    alt="QR Code"
                    width="120"
                    height="120"
                  />
                </div>
              )}
            </div>

            <div className={styles.authDetails}>
              <div className={styles.inputGroup}>
                <label>{t('settings.security.twoFa.copyInstruction')}</label>
                <div className={styles.inputWrapper}>
                  {isSetupLoading || !twoFASetupData?.secret ? (
                    <Skeleton
                      animation="wave"
                      variant="rounded"
                      width="100%"
                      height={40}
                      sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                    />
                  ) : (
                    <>
                      <input
                        type="text"
                        className={styles.inputField}
                        value={twoFASetupData.secret}
                        readOnly
                      />
                      <button
                        className={styles.copyBtn}
                        aria-label="Copy Code"
                        onClick={() => {
                          if (twoFASetupData?.secret) {
                            navigator.clipboard.writeText(twoFASetupData.secret);
                            dispatch(showToast({ text: t('settings.security.twoFa.copied'), type: 'success' }));
                          }
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className={styles.inputGroup} style={{ marginTop: '20px' }}>
                <label>{t('settings.security.twoFa.twoFactorCode')}</label>
                <div className={styles.inputWrapper}>
                  <input
                    value={twoFactorCode}
                    type="text"
                    className={styles.inputField}
                    placeholder="XXXXXX"
                    onChange={(e) => {
                      setTwoFactorCode(e.target.value);
                      setApiErrorTwoFA('');
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <ActionButton
            className={styles.activateBtn}
            disabled={twoFactorCode === ''}
            isLoading={isTwoFALoading}
            onClick={handleActivate2FA}
          >
            {t('settings.security.twoFa.activateBtn')}
          </ActionButton>
        </>
      )}
    </div>
  );
};

export default TwoFactor;
